import { useMemo, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

export function Signup() {
  const { signup, status, activeOrganisation } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [organisationName, setOrganisationName] = useState("");
  const [organisationSlug, setOrganisationSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [isSupplier, setIsSupplier] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const derivedSlug = useMemo(() => slugify(organisationName), [organisationName]);
  const effectiveSlug = slugTouched ? organisationSlug : derivedSlug;

  if (status === "authenticated" && activeOrganisation) {
    return <Navigate to={`/o/${activeOrganisation.slug}/dashboard`} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    try {
      await signup({
        fullName,
        email,
        password,
        organisationName,
        organisationSlug: effectiveSlug,
        isSupplier,
      });
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      id="main"
      className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900"
    >
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Create your ISO Clarity organisation
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
          >
            Sign in
          </Link>
        </p>

        <form onSubmit={onSubmit} className="mt-6 grid gap-4" noValidate>
          <div>
            <label
              htmlFor="signup-name"
              className="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Your full name
            </label>
            <input
              id="signup-name"
              type="text"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              aria-invalid={errors.length > 0 || undefined}
              aria-describedby={errors.length > 0 ? "signup-errors" : undefined}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <div>
            <label
              htmlFor="signup-email"
              className="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Work email
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={errors.length > 0 || undefined}
              aria-describedby={errors.length > 0 ? "signup-errors" : undefined}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <div>
            <label
              htmlFor="signup-password"
              className="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Password
            </label>
            <div className="mt-1 flex">
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="signup-password-hint"
                className="block w-full rounded-l-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                className="rounded-r-md border border-l-0 border-slate-300 bg-slate-50 px-3 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <p
              id="signup-password-hint"
              className="mt-1 text-xs text-slate-500 dark:text-slate-400"
            >
              At least 12 characters, including upper- and lower-case letters and a digit.
            </p>
          </div>

          <div>
            <label
              htmlFor="signup-org-name"
              className="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Organisation name
            </label>
            <input
              id="signup-org-name"
              type="text"
              required
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              aria-invalid={errors.length > 0 || undefined}
              aria-describedby={errors.length > 0 ? "signup-errors" : undefined}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <fieldset className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/40">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Organisation type
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-white dark:hover:bg-slate-800">
              <input
                type="radio"
                name="signup-supplier"
                checked={!isSupplier}
                onChange={() => setIsSupplier(false)}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                <span className="block font-medium">
                  We use ISO Clarity to run our own management system
                </span>
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  Full IMS — clauses, evidence vault, audit log, nonconformities. Choose modules
                  later.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-white dark:hover:bg-slate-800">
              <input
                type="radio"
                name="signup-supplier"
                checked={isSupplier}
                onChange={() => setIsSupplier(true)}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                <span className="block font-medium">
                  I&rsquo;m a supplier — I just want to share evidence with our buyers
                </span>
                <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                  Free Supplier tier. You can publish authorised certificates and policies; buyers
                  who connect with you can pin them to their own vault. Upgrade any time to unlock
                  the full IMS.
                </span>
              </span>
            </label>
          </fieldset>

          <div>
            <label
              htmlFor="signup-org-slug"
              className="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              URL slug
            </label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                isoclarity.co.uk/o/
              </span>
              <input
                id="signup-org-slug"
                type="text"
                required
                pattern="^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$"
                value={effectiveSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setOrganisationSlug(e.target.value);
                }}
                aria-describedby="signup-slug-hint"
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
            <p id="signup-slug-hint" className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              3-40 lowercase characters, digits and hyphens only.
            </p>
          </div>

          {errors.length > 0 ? (
            <div
              id="signup-errors"
              role="alert"
              className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
            >
              {errors.length === 1 ? (
                errors[0]
              ) : (
                <ul className="list-disc space-y-0.5 pl-4">
                  {errors.map((message, idx) => (
                    <li key={idx}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create organisation"}
          </button>
        </form>
      </div>
    </main>
  );
}
